import { MediaBlock } from '@/ui/blocks/MediaBlock'
import {
  DefaultNodeTypes,
  SerializedAutoLinkNode,
  SerializedBlockNode,
  SerializedLinkNode,
} from '@payloadcms/richtext-lexical'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import {
  JSXConverter,
  JSXConvertersFunction,
  LinkJSXConverter,
  RichText as RichTextWithoutBlocks,
} from '@payloadcms/richtext-lexical/react'

import { CodeBlock, CodeBlockProps } from '@/ui/blocks/Code/Component'

import type {
  CallToActionBlock as CTABlockProps,
  LinkCardBlock as LinkCardBlockProps,
  MediaBlock as MediaBlockProps,
  PdfBlock as PdfBlockProps,
} from '@/payload-types'
import { cn } from '@/utils/ui'
import { LinkCardBlock } from '@/ui/blocks/LinkCardBlock/Component'
import { PdfBlock } from '@/ui/blocks/PdfBlock/component'
import { GatedFileLink } from './GatedFileLink'
import { isGatedFileUrl } from '@/lib/downloads/fileLinks'

type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<
      CTABlockProps | MediaBlockProps | CodeBlockProps | LinkCardBlockProps | PdfBlockProps
    >

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
  const { value, relationTo } = linkNode.fields.doc!
  if (typeof value !== 'object') {
    throw new Error('Expected value to be an object')
  }
  // Prefer the document's real stored `path` (e.g. `/workflows/...` or
  // `/products/...`). The previous `/${slug}` guess produced wrong
  // URLs for every collection whose path isn't a bare slug. The `/en` locale
  // prefix is added by the i18n middleware.
  const path = (value as { path?: string | null }).path
  if (typeof path === 'string' && path.length > 0) return path
  const slug = (value as { slug?: string }).slug
  return relationTo === 'posts' ? `/posts/${slug}` : `/${slug}`
}

// Downloads are members-only platform-wide (Veronika, 2026-08-26 / 2026-09-16). Every link
// converter goes through this wrapper: a link whose target is a file renders as the gated
// GatedFileLink instead of a plain anchor; everything else falls through to Payload's own
// converter. Covers custom URLs, autolinks and internal links to media documents.
const baseLinkConverters = LinkJSXConverter({ internalDocToHref })
// JSXConverter is `function | ReactNode`; we only ever deal with the function member
type ConverterFn<N extends SerializedLinkNode | SerializedAutoLinkNode> = Extract<
  JSXConverter<N>,
  (...args: never[]) => unknown
>
type LinkFields = SerializedLinkNode['fields'] & { doc?: { relationTo?: string; value?: unknown } }
const fileTargetOf = (fields: LinkFields): string | null => {
  if (fields.linkType === 'internal') {
    const value = fields.doc?.value
    const url = value && typeof value === 'object' ? (value as { url?: unknown }).url : undefined
    return fields.doc?.relationTo === 'media' && typeof url === 'string' && isGatedFileUrl(url) ? url : null
  }
  return isGatedFileUrl(fields.url) ? String(fields.url) : null
}
function gatedLinkConverter<N extends SerializedLinkNode | SerializedAutoLinkNode>(
  kind: 'link' | 'autolink',
): ConverterFn<N> {
  const GatedLinkConverter = (args: Parameters<ConverterFn<N>>[0]) => {
    const file = fileTargetOf(args.node.fields as LinkFields)
    if (file) {
      return <GatedFileLink href={file}>{args.nodesToJSX({ nodes: args.node.children })}</GatedFileLink>
    }
    return (baseLinkConverters[kind] as ConverterFn<N>)(args)
  }
  return GatedLinkConverter as ConverterFn<N>
}

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  ...baseLinkConverters,
  link: gatedLinkConverter<SerializedLinkNode>('link'),
  autolink: gatedLinkConverter<SerializedAutoLinkNode>('autolink'),
  blocks: {
    mediaBlock: ({ node }) => (
      <div className="mb-10 last:mb-0">
        <MediaBlock className="" {...node.fields} />
      </div>
    ),
    code: ({ node }) => (
      <div className="mb-10 last:mb-0">
        <CodeBlock className="col-start-2" {...node.fields} />
      </div>
    ),
    linkCardBlock: ({ node }) => (
      <div className="mb-10 last:mb-0">
        <LinkCardBlock {...node.fields} />
      </div>
    ),
    pdfBlock: ({ node }) => (
      <div className="mb-10 last:mb-0">
        <PdfBlock {...node.fields} />
      </div>
    ),
    //cta: ({ node }) => <CallToActionBlock {...node.fields} />,
  },
})

type Props = {
  data: SerializedEditorState
  enableGutter?: boolean
  enableProse?: boolean
  variant?: 'default' | 'plain'
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
  const { className, enableProse = true, enableGutter = true, variant = 'default', ...rest } = props

  const isPlain = variant === 'plain'

  return (
    <RichTextWithoutBlocks
      converters={jsxConverters}
      className={cn(
        {
          'max-w-none': !enableGutter || isPlain,
          'mx-auto prose md:prose-md': !isPlain && enableProse,
          'prose-p:text-body-M-regular prose-p:!mt-0 prose-p:!mb-4 [&>*]:mb-10 [&>*:last-child]:!mb-0 prose-headings:!mt-0 prose-h1:text-headline-L-semibold prose-h1:font-bold prose-h1:leading-[130%] lg:prose-h1:!mb-8 prose-h1:!mb-12 prose-h2:text-headline-M-semibold prose-h2:font-bold prose-h2:leading-[130%] lg:prose-h2:!mb-8 prose-h2:!mb-5 prose-h3:text-headline-S-semibold prose-h3:!mb-4 prose-h4:text-title-M-medium prose-h4:!mb-4 prose-h5:text-title-M-regular prose-h5:!mb-4 prose-h6:text-title-S-medium prose-h6:!mb-2':
            !isPlain && enableProse,
          '[&_ul]:!list-disc [&_ul]:!pl-6 [&_ul]:!mb-4': !isPlain && enableProse,
          '[&_ul>li]:!text-text-base [&_ul>li]:!mb-2 [&_ul>li]:marker:!text-text-base':
            !isPlain && enableProse,
        },
        className,
      )}
      {...rest}
    />
  )
}
